import momentTimezone from 'moment-timezone';

import { config } from 'src/config/config';
import i18n from 'src/config/i18n';
import { RegisteredUserInfo } from 'src/modules/user/models/user';
import { CustomError, httpClient } from 'src/config/http';
import { ToastNotificationsService } from 'src/shared/services/ToastNotificationsService';
import { REGISTRATION_ERROR } from '../constants';

import {
    CredentialsWithUserInfo,
    CredentialsWithUserInfoAndTZ,
    ResetPasswordRequiredInfo,
    SingleSignOnRequestData,
    SingleSignOnRequestResponse,
    MemberAccessFlowResponse,
} from '../models/auth';

export class UserAuthenticator {
    constructor(private toastNotificationsService: ToastNotificationsService) {}

    public async validateEmailAndCompany(
        email: string,
        companyCode: string,
    ): Promise<{ companyName?: string }> {
        try {
            return await httpClient.POST_UNAUTHENTICATED('accounts/validation/', {
                email,
                companyCode,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            // error ALWAYS looks like { detail: [some, values] }
            const errors = Object.values(error.body.detail).map(value => value);
            return Promise.reject(errors);
        }
    }

    public async checkEligibility(data: Record<string, string>): Promise<void> {
        try {
            await httpClient.POST_UNAUTHENTICATED('accounts/eligibility/', {
                ...data,
                company: data.companyCode.toLowerCase(),
                dateOfBirth: data.birthDate,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            return this.parseAccountResponseError(error);
        }
    }

    public async createAccount(credentials: CredentialsWithUserInfo): Promise<void> {
        const localTimezone = momentTimezone.tz.guess() || null;

        try {
            await httpClient.POST_UNAUTHENTICATED<RegisteredUserInfo, CredentialsWithUserInfoAndTZ>(
                'accounts/',
                {
                    ...credentials,
                    localTimezone,
                },
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const noErrorMessageReceived = error.body === undefined || error.body === null;

            // Flag to catch ANY eligible error NOT RAISED BY INELIGIBILITY
            const genericEligibleError =
                error.code === 403 &&
                !noErrorMessageReceived &&
                !!error.body.detail &&
                error.body.detail.toUpperCase() !== 'INELIGIBLE';

            // Flag to catch error RAISED BY INELIGIBILITY
            const ineligibleError =
                error.code === 403 &&
                !noErrorMessageReceived &&
                !!error.body.detail &&
                error.body.detail.toUpperCase() === 'INELIGIBLE';

            const missingLastName = !noErrorMessageReceived && !!error.body.lastName;

            const notificationMessage = genericEligibleError
                ? i18n.t('auth:registration_form.errors.use_credentials')
                : missingLastName
                ? i18n.t('auth:registration_form.errors.use_credentials')
                : noErrorMessageReceived
                ? i18n.t(config.messages.genericError)
                : '';

            const errors =
                genericEligibleError || noErrorMessageReceived
                    ? []
                    : ineligibleError
                    ? ['INELIGIBLE']
                    : Object.keys(error.body).map(value => value);

            if (notificationMessage) {
                this.toastNotificationsService.renderErrorToast(notificationMessage);
            }

            return Promise.reject(errors);
        }
    }

    private async parseAccountResponseError(error: CustomError): Promise<void> {
        // Besides error.body.lastName, error looks like { detail: "SOME_VALUE" }
        const noErrorMessageReceived = error.body === undefined || error.body === null;

        // Flag to catch ANY eligible error NOT RAISED BY INELIGIBILITY
        const hasGenericEligibleError =
            error.code === 403 &&
            !noErrorMessageReceived &&
            !!error.body?.detail &&
            error.body?.detail === REGISTRATION_ERROR;

        // Not sure if this is used
        const missingLastName = !noErrorMessageReceived && !!error.body?.lastName;

        const message =
            hasGenericEligibleError || missingLastName
                ? i18n.t('auth:registration_form.errors.use_credentials')
                : noErrorMessageReceived
                ? i18n.t(config.messages.genericError)
                : '';

        const errors = message ? [] : Object.values(error.body ?? []).map(value => value);

        if (message) {
            this.toastNotificationsService.renderErrorToast(message);
        }

        return Promise.reject(errors);
    }

    public async requestPasswordResetEmail(email: string): Promise<void> {
        try {
            await httpClient.POST_UNAUTHENTICATED('password/reset/', {
                email,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMsg =
                error.body && error.body.email
                    ? error.body.email[0]
                    : i18n.t(config.messages.connectionProblemError);

            return Promise.reject(errorMsg);
        }
    }

    public async setNewPassword(data: ResetPasswordRequiredInfo): Promise<void> {
        try {
            await httpClient.POST_UNAUTHENTICATED('password/reset/done/', data);

            this.toastNotificationsService.renderSuccessToast(
                'Your new password has now been set and you are able to log in.',
            );
        } catch {
            this.toastNotificationsService.renderErrorToast(config.messages.connectionProblemError);
        }
    }

    public async singleSignOnRequest(data: SingleSignOnRequestData): Promise<void> {
        try {
            const { ssoUrl } = await httpClient.GET_UNAUTHENTICATED<SingleSignOnRequestResponse>(
                `sso_settings?email=${encodeURIComponent(data.email)}`,
            );

            if (!ssoUrl) {
                return Promise.reject(i18n.t('notifications.singleSignOnNotEligible'));
            }

            window.location.replace(ssoUrl);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMsg =
                error.code >= 500
                    ? config.messages.connectionProblemError
                    : i18n.t('notifications.singleSignOnNotEligible');

            return Promise.reject(errorMsg);
        }
    }

    public async checkMemberAccessFlow(email: string): Promise<MemberAccessFlowResponse> {
        try {
            return await httpClient.GET_UNAUTHENTICATED<MemberAccessFlowResponse>(
                `member_access_flow?email=${encodeURIComponent(email)}`,
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            const errorMsg =
                error.code >= 500
                    ? config.messages.connectionProblemError
                    : config.messages.genericError;

            throw new Error(errorMsg);
        }
    }
}
